import type {
  MachineBridgeAnswer,
  MachineInventory,
  PluginId,
  PluginRoster,
  SettledJob,
} from "@manifold/protocol";
import type { EmitEvent } from "./emit.ts";
import type { PluginDatabase } from "./database.ts";
import type { PluginStorage } from "./storage.ts";
import type { PluginActionContext, PluginJobContext, PluginServiceContext } from "./runtime.ts";

/**
 * THE LIFECYCLE — four hooks, one bound, no veto.
 *
 * A plugin gets told when it starts serving, when it stops, when the composition around it
 * moved, and when its data is being destroyed. What it never gets is a vote: no hook can
 * refuse or delay the transition that fired it. Enablement is workspace-global shared state
 * (D4), so a hook that could block one would let one plugin hold a whole workspace's
 * composition hostage — the failure mode every surveyed platform that allows it eventually
 * grows a timeout to escape.
 *
 * The default bound is 2 seconds per hook (`LIFECYCLE_TIMEOUT_MS`). Trusted host startup
 * policy may extend only selected plugins' `onJobSettled`, within a finite 60-second ceiling.
 * Transition hooks keep this default. Past the bound the engine stops WAITING; it cannot
 * stop in-realm code or undo already-admitted effects. A settled-job context loses
 * authority when its own lease ends. The
 * roster records what happened (`lifecycle: "enable_failed" | "disable_failed"`) so a failed
 * hook is visible rather than swallowed, and a DISABLE always completes regardless: the
 * remedy for a plugin misbehaving on the way out must not be that plugin.
 */
export const LIFECYCLE_TIMEOUT_MS = 2_000;

/**
 * What a hook is handed: its own identity, its own storage, its own tables when it declared
 * any, the server's clock, the one emission call, and — when the row's installer still has
 * restorable authority — jobs, dependency actions and read-only metadata. Every effect still
 * goes through its existing authority owner (AXIOMS.md §The plane rule).
 *
 * `emit` is not an exception to that rule, it is the shape of it: an event NOTIFIES and never
 * mutates, so handing a hook the ability to say "I am serving now" costs nothing a door would
 * have had to guard. What a hook still cannot do is change anything — including refusing or
 * delaying the transition that fired it.
 *
 * `jobs` is the same admission as `onJobSettled`'s, one step earlier: a plugin that OWNS a
 * cadence has to be able to register it when it is turned on, rather than waiting for a
 * dispatch or a settlement that may never arrive for a half nobody opens (#514). It is bound
 * to the INSTALLER's credential — the authority that consented to the row — restored and
 * rechecked at every fan-out, so it is neither ambient plugin authority nor the enabling
 * administrator's. It is OPTIONAL because that restore can fail: a revoked or expired
 * installer, or a first-party row nobody installed, leaves the slice absent rather than
 * silently downgraded, and a plugin that needs it says so by checking.
 *
 * `actions` is admitted on exactly the same terms and for the same reason (ADR 0041): a half
 * that composes on a sibling has to be able to ask it something at the transition it owns,
 * and the principal it asks under is the installer's — the one authority the row has when
 * nobody is dispatching — so the two slices are present or absent together.
 *
 * `host`, `machines` and `services` share that credential, never the enabling administrator's.
 * Their narrow reads recheck the current manifest, install grant, credential and hook lifetime
 * at use. Roster and fleet inventory require `containers:read`; instance listing requires
 * `services:read` and retains per-service visibility. No machine effect, policy, secret,
 * invocation or configuration handle is exposed. A settled hook uses its job's credential.
 *
 * The parameter is contravariant, so a plugin may declare the minimal slice it actually uses
 * (`(ctx: { storage: PluginStorage }) => void`) and still satisfy the hook type. That is the
 * same sandbox shape the server's action handlers use, checked at the registration site — and
 * it is why a hook that never emits never mentions `emit`.
 */
export interface LifecycleCtx {
  readonly pluginId: string;
  readonly storage: PluginStorage;
  /**
   * This plugin's own tables (ADR 0034), present exactly when its manifest declares
   * `database`. A plugin that declared none has no slice here — the member is absent, not an
   * empty handle — which is why a hook that uses it declares it and every hook written before
   * the file existed keeps type-checking unchanged.
   */
  readonly database?: PluginDatabase;
  readonly emit: EmitEvent;
  readonly jobs?: PluginJobContext | undefined;
  readonly actions?: PluginActionContext | undefined;
  readonly host?:
    | {
        roster(): PluginRoster;
        enabled(id: string): boolean;
      }
    | undefined;
  readonly services?: Pick<PluginServiceContext, "listInstances"> | undefined;
  readonly machines?: { inventory(): MachineBridgeAnswer<MachineInventory> } | undefined;
  now(): number;
}

/**
 * What changed in one roster commit, as the surviving plugins are told. Both lists are in
 * composition (topological) order, so a plugin reading them sees dependencies before
 * dependents, exactly as the fan-out itself is ordered.
 */
export interface AssemblyDelta {
  readonly enabled: readonly PluginId[];
  readonly disabled: readonly PluginId[];
}

export type LifecycleHook = (ctx: LifecycleCtx) => void | Promise<void>;
export type AssemblyChangedHook = (ctx: LifecycleCtx, delta: AssemblyDelta) => void | Promise<void>;
/**
 * What `onJobSettled` is handed beyond an ordinary hook ctx: the job slice, bound to the
 * CREDENTIAL THE JOB RAN UNDER, restored and rechecked at delivery. It is REQUIRED here where
 * it is optional above, and it is the job's rather than the installer's — a revoked or expired
 * credential simply has no wake to deliver, so a settled hook that runs at all has the
 * authority its own job ran under, and every read through it still discharges consent and
 * grants the way a door-dispatched one does (ADR 0033 §Execution and authority).
 */
export interface JobSettledCtx extends LifecycleCtx {
  readonly jobs: PluginJobContext;
  /** The sibling verb, bound to the same credential the job ran under (ADR 0041). */
  readonly actions: PluginActionContext;
}
export type JobSettledHook = (ctx: JobSettledCtx, job: SettledJob) => void | Promise<void>;

/**
 * The hooks a plugin may declare. Every one is optional and most plugins declare none: a
 * plugin whose whole state lives in the documents, the roster and its actions has nothing to
 * do at a transition, and the engine must not invent work for it.
 *
 * `onEnable` and `onDisable` are TRANSITION hooks, not boot activation. At boot everything
 * enabled is simply live — there was no transition, so no hook fires and no lifecycle state
 * is inferred. This keeps process start free of a fan-out whose failures nobody asked for,
 * and keeps "the hook ran" meaning "somebody changed something".
 *
 * `onPurge` fires from the purge door only — never from a disable. A disable RETAINS data
 * (the residual mechanism is `retain`; there is no erase-on-disable), and destruction is a
 * separate, explicitly named verb.
 *
 * `onJobSettled` is the ONE hook that is not a composition transition: a job the plugin
 * started reached a terminal state, and its owner is told once, under the same bound and the
 * same no-veto rule. It fires for the OWNING plugin only and only while that plugin is
 * enabled — a disabled plugin's jobs are cancelled, not delivered — and it carries safe
 * metadata, so reading the bytes is still `ctx.jobs.outputs` under current authority.
 */
export interface PluginLifecycle {
  readonly onEnable?: LifecycleHook;
  readonly onDisable?: LifecycleHook;
  readonly onAssemblyChanged?: AssemblyChangedHook;
  readonly onJobSettled?: JobSettledHook;
  readonly onPurge?: LifecycleHook;
}

/** Whether a hook settled inside its bound, and why not when it did not. */
export type HookOutcome = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/**
 * Runs one hook under the bound. A throw, a rejection and an overrun are the SAME outcome to
 * the caller — the transition proceeds and the failure is named — because the engine's
 * obligation is to complete the transition, not to diagnose the plugin.
 *
 * A timed-out hook keeps running: there is no way to cancel someone else's promise, and the
 * honest report is that the engine stopped waiting.
 */
export async function runHook(
  invoke: () => void | Promise<void>,
  timeoutMs: number = LIFECYCLE_TIMEOUT_MS,
): Promise<HookOutcome> {
  // A cancellation CLOSURE rather than a handle: the same trick `RoomTimers` uses, so this
  // module never names a platform-specific timer type.
  let cancel = (): void => {};
  try {
    const settled = await Promise.race([
      (async () => {
        await invoke();
        return "settled" as const;
      })(),
      new Promise<"timeout">((resolve) => {
        const handle = setTimeout(() => resolve("timeout"), timeoutMs);
        cancel = (): void => {
          clearTimeout(handle);
        };
      }),
    ]);
    if (settled === "timeout") {
      return { ok: false, reason: `did not settle within ${String(timeoutMs)}ms` };
    }
    return { ok: true };
  } catch (reason) {
    return { ok: false, reason: reason instanceof Error ? reason.message : String(reason) };
  } finally {
    cancel();
  }
}
